<template>
  <section id="huay-dashboard">
    <!-- Agent Filter (Master only) -->
    <b-card
      v-if="isMaster"
      body-class="py-75 px-1"
    >
      <div
        class="d-flex flex-wrap align-items-center"
        style="gap: 12px;"
      >
        <div
          class="agent-select-wrapper"
          style="min-width: 250px; max-width: 350px; flex: 1;"
        >
          <AgentSelector
            v-model="selectedAgent"
            :placeholder="$t('เลือกตัวแทน...')"
            @change="onAgentChange"
          />
        </div>
        <b-form-checkbox
          v-model="includeDownlines"
          switch
          :disabled="!selectedAgent || !selectedAgent.code || selectedAgent.code === 'master'"
          class="agent-filter-checkbox"
          @change="reloadAll"
        >
          {{ $t('รวมตัวแทนย่อย') }}
        </b-form-checkbox>
      </div>
    </b-card>

    <!-- 3 Summary Cards (Gradient) -->
    <b-row>
      <!-- ยอดแทงวันนี้ -->
      <b-col
        md="4"
        sm="4"
        cols="12"
      >
        <div class="summary-card summary-card-bet">
          <div class="summary-card-body">
            <div class="summary-card-left">
              <div class="summary-card-avatar">
                <feather-icon
                  icon="PlusIcon"
                  size="18"
                />
              </div>
              <div
                v-if="!loadingMiniCharts && sevenDaysData.bet && sevenDaysData.bet.length > 0"
                class="mini-bar-chart"
              >
                <div
                  v-for="(height, index) in getMiniChartHeights(sevenDaysData.bet)"
                  :key="index"
                  class="mini-bar"
                  :style="{ height: height + '%' }"
                />
              </div>
              <div
                v-if="loadingMiniCharts"
                class="mini-bar-chart"
              >
                <div
                  v-for="i in 7"
                  :key="i"
                  class="mini-bar skeleton-bar"
                  :style="{ height: (20 + i * 10) + '%' }"
                />
              </div>
            </div>
            <div class="summary-card-right">
              <div class="summary-card-header">
                {{ $t('ยอดแทงวันนี้') }}
              </div>
              <div
                v-if="loadingOverview"
                class="summary-card-value"
              >
                <b-skeleton
                  width="80%"
                  height="30px"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
              <div
                v-if="loadingOverview"
                class="summary-card-change"
              >
                <b-skeleton
                  width="50%"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
              <template v-if="!loadingOverview">
                <div class="summary-card-value">
                  {{ toNumber(Math.round(todaySum.bet)) }}
                </div>
                <div class="summary-card-change">
                  <span style="font-size:11px;font-weight:900;">
                    {{ todayBetChange >= 0 ? '⬆' : '⬇' }} {{ Math.abs(todayBetChange) }}%
                  </span>
                  <span style="font-size:9px;margin-left:3px;">{{ $t('วันนี้') }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </b-col>
      <!-- ยอดจ่ายวันนี้ -->
      <b-col
        md="4"
        sm="4"
        cols="12"
      >
        <div class="summary-card summary-card-pay">
          <div class="summary-card-body">
            <div class="summary-card-left">
              <div class="summary-card-avatar">
                <feather-icon
                  icon="MinusIcon"
                  size="18"
                />
              </div>
              <div
                v-if="!loadingMiniCharts && sevenDaysData.pay && sevenDaysData.pay.length > 0"
                class="mini-bar-chart"
              >
                <div
                  v-for="(height, index) in getMiniChartHeights(sevenDaysData.pay)"
                  :key="index"
                  class="mini-bar"
                  :style="{ height: height + '%' }"
                />
              </div>
              <div
                v-if="loadingMiniCharts"
                class="mini-bar-chart"
              >
                <div
                  v-for="i in 7"
                  :key="i"
                  class="mini-bar skeleton-bar"
                  :style="{ height: (30 + i * 8) + '%' }"
                />
              </div>
            </div>
            <div class="summary-card-right">
              <div class="summary-card-header">
                {{ $t('ยอดจ่ายวันนี้') }}
              </div>
              <div
                v-if="loadingOverview"
                class="summary-card-value"
              >
                <b-skeleton
                  width="80%"
                  height="30px"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
              <div
                v-if="loadingOverview"
                class="summary-card-change"
              >
                <b-skeleton
                  width="50%"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
              <template v-if="!loadingOverview">
                <div class="summary-card-value">
                  {{ toNumber(Math.round(todaySum.pay)) }}
                </div>
                <div class="summary-card-change">
                  <span style="font-size:11px;font-weight:900;">
                    {{ todayPayChange >= 0 ? '⬆' : '⬇' }} {{ Math.abs(todayPayChange) }}%
                  </span>
                  <span style="font-size:9px;margin-left:3px;">วันนี้</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </b-col>
      <!-- กำไรวันนี้ -->
      <b-col
        md="4"
        sm="4"
        cols="12"
      >
        <div class="summary-card summary-card-profit">
          <div class="summary-card-body">
            <div class="summary-card-left">
              <div class="summary-card-avatar">
                <feather-icon
                  icon="DollarSignIcon"
                  size="18"
                />
              </div>
              <div
                v-if="!loadingMiniCharts && sevenDaysData.income && sevenDaysData.income.length > 0"
                class="mini-bar-chart"
              >
                <div
                  v-for="(height, index) in getMiniChartHeights(sevenDaysData.income)"
                  :key="index"
                  class="mini-bar"
                  :style="{ height: height + '%' }"
                />
              </div>
              <div
                v-if="loadingMiniCharts"
                class="mini-bar-chart"
              >
                <div
                  v-for="i in 7"
                  :key="i"
                  class="mini-bar skeleton-bar"
                  :style="{ height: (25 + i * 9) + '%' }"
                />
              </div>
            </div>
            <div class="summary-card-right">
              <div class="summary-card-header">
                กำไรวันนี้
              </div>
              <div
                v-if="loadingOverview"
                class="summary-card-value"
              >
                <b-skeleton
                  width="80%"
                  height="30px"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
              <div
                v-if="loadingOverview"
                class="summary-card-change"
              >
                <b-skeleton
                  width="50%"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
              <template v-if="!loadingOverview">
                <div class="summary-card-value">
                  {{ toNumber(Math.round(todaySum.income)) }}
                </div>
                <div class="summary-card-change">
                  <span style="font-size:11px;font-weight:900;">
                    {{ todayIncomeChange >= 0 ? '⬆' : '⬇' }} {{ Math.abs(todayIncomeChange) }}%
                  </span>
                  <span style="font-size:9px;margin-left:3px;">วันนี้</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Player Insights + Gov Lottery -->
    <b-row>
      <!-- รายการโพย -->
      <b-col
        md="4"
        sm="4"
        cols="12"
      >
        <b-card
          no-body
          class="insight-card"
        >
          <b-card-body>
            <template v-if="loadingOverview">
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <b-skeleton
                  width="40%"
                  class="mb-0"
                />
                <b-skeleton
                  width="25%"
                  height="38px"
                  class="mb-0"
                />
              </div>
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <b-skeleton
                  width="45%"
                  class="mb-0"
                />
                <b-skeleton
                  width="20%"
                  height="38px"
                  class="mb-0"
                />
              </div>
            </template>
            <template v-else>
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <span class="insight-label d-sm-none d-xl-inline">รายการโพยทั้งหมด</span>
                <span class="insight-label d-none d-sm-inline d-xl-none">โพยทั้งหมด</span>
                <span class="insight-value">
                  {{ toNumber(playerInsights.totalPoy) }}
                </span>
              </div>
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <span class="insight-label d-sm-none d-xl-inline">รายการโพยต่อคน</span>
                <span class="insight-label d-none d-sm-inline d-xl-none">โพยต่อคน</span>
                <span class="insight-value">
                  {{ toNumber(playerInsights.poyPerUser) }}
                </span>
              </div>
            </template>
          </b-card-body>
        </b-card>
      </b-col>
      <!-- Big Spender -->
      <b-col
        md="4"
        sm="4"
        cols="12"
      >
        <b-card
          no-body
          class="insight-card"
        >
          <b-card-body>
            <template v-if="loadingOverview">
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <b-skeleton
                  width="35%"
                  class="mb-0"
                />
                <b-skeleton
                  width="20%"
                  height="38px"
                  class="mb-0"
                />
              </div>
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <b-skeleton
                  width="40%"
                  class="mb-0"
                />
                <b-skeleton
                  width="25%"
                  height="38px"
                  class="mb-0"
                />
              </div>
            </template>
            <template v-else>
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <span class="insight-label">Big Spender</span>
                <span class="insight-value">
                  {{ toNumber(playerInsights.bigSpender) }}
                </span>
              </div>
              <div
                class="d-flex justify-content-between align-items-center"
                style="padding:8px 0;"
              >
                <span class="insight-label">แทงเฉลี่ย/คน</span>
                <span class="insight-value">
                  {{ toNumber(Math.round(playerInsights.avgBetPerUser)) }}
                </span>
              </div>
            </template>
          </b-card-body>
        </b-card>
      </b-col>
      <!-- หวยรัฐบาล -->
      <b-col
        md="4"
        sm="4"
        cols="12"
      >
        <div
          class="gov-lottery-card cursor-pointer"
          @click="showGovLotteryModal = true"
        >
          <template v-if="loadingOverview">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <b-skeleton
                  width="80px"
                  height="14px"
                  class="skeleton-light mb-0"
                />
                <b-skeleton
                  width="60px"
                  height="11px"
                  class="skeleton-light mb-0"
                  style="margin-top:2px;"
                />
              </div>
              <div class="text-right">
                <b-skeleton
                  width="40px"
                  height="10px"
                  class="skeleton-light mb-0 ml-auto"
                />
                <b-skeleton
                  width="90px"
                  height="18px"
                  class="skeleton-light mb-0 ml-auto"
                />
                <b-skeleton
                  width="35px"
                  height="10px"
                  class="skeleton-light mb-0 ml-auto"
                />
              </div>
            </div>
            <hr style="border-color:rgba(255,255,255,0.3);margin:6px 0;">
            <div class="d-flex">
              <div class="flex-fill">
                <b-skeleton
                  width="40px"
                  height="10px"
                  class="skeleton-light mb-0"
                />
                <b-skeleton
                  width="90px"
                  height="16px"
                  class="skeleton-light mb-0"
                  style="margin-top:2px;"
                />
              </div>
              <div class="flex-fill text-right">
                <b-skeleton
                  width="30px"
                  height="10px"
                  class="skeleton-light mb-0 ml-auto"
                />
                <b-skeleton
                  width="90px"
                  height="16px"
                  class="skeleton-light mb-0 ml-auto"
                  style="margin-top:2px;"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div style="font-size:14px;font-weight:600;line-height:1.2;display:flex;align-items:center;gap:4px;">
                  <span>หวยรัฐบาล</span>
                  <feather-icon
                    v-b-tooltip.hover="'คลิกเพื่อดูงวดอื่นๆ'"
                    icon="ExternalLinkIcon"
                    size="13"
                    class="gov-lottery-icon"
                  />
                </div>
                <div style="font-size:11px;line-height:1.2;margin-top:2px;">
                  {{ $t('งวด') }} {{ govLottery.roundDate || '-' }}
                </div>
              </div>
              <div class="text-right">
                <div style="font-size:10px;line-height:1;margin-bottom:2px;">
                  {{ $t('ยอดแทง') }}
                </div>
                <div style="font-size:18px;font-weight:700;line-height:1;">
                  <span class="d-sm-none d-xl-inline">{{ toNumber(govLottery.bet) }}</span>
                  <span class="d-none d-sm-inline d-xl-none">{{ shortNumber(govLottery.bet) }}</span>
                </div>
                <div style="font-size:10px;line-height:1;margin-top:2px;">
                  <span :style="{ fontWeight: 600, color: govLottery.betChangePercent > 0 ? '#69f0ae' : govLottery.betChangePercent < 0 ? '#ef9a9a' : '#fff' }">
                    {{ govLottery.betChangePercent > 0 ? '+' : '' }}{{ govLottery.betChangePercent }}%
                  </span>
                </div>
              </div>
            </div>
            <hr style="border-color:rgba(255,255,255,0.3);margin:6px 0;">
            <div
              class="d-flex"
              style="position:relative;"
            >
              <div class="flex-fill">
                <div style="font-size:10px;line-height:1;">
                  {{ $t('ยอดจ่าย') }}
                </div>
                <div style="font-size:16px;font-weight:600;line-height:1.2;margin-top:2px;">
                  <span class="d-sm-none d-xl-inline">{{ toNumber(govLottery.pay) }}</span>
                  <span class="d-none d-sm-inline d-xl-none">{{ shortNumber(govLottery.pay) }}</span>
                </div>
              </div>
              <div style="width:1px;background:rgba(255,255,255,0.3);position:absolute;left:50%;top:0;bottom:0;" />
              <div class="flex-fill text-right">
                <div style="font-size:10px;line-height:1;">
                  กำไร
                </div>
                <div
                  style="font-size:16px;font-weight:600;line-height:1.2;margin-top:2px;"
                  :style="{ color: govLottery.profit > 0 ? '#69f0ae' : govLottery.profit < 0 ? '#ef9a9a' : '#fff' }"
                >
                  <span class="d-sm-none d-xl-inline">{{ toNumber(govLottery.profit) }}</span>
                  <span class="d-none d-sm-inline d-xl-none">{{ shortNumber(govLottery.profit) }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </b-col>
    </b-row>

    <!-- Chart (col-8) + MTD (col-4) -->
    <b-row class="match-height">
      <!-- Main Chart -->
      <b-col
        md="8"
        cols="12"
      >
        <b-card body-class="d-flex flex-column">
          <div class="d-flex justify-content-between align-items-center mb-1">
            <b-card-title class="mb-0">
              {{ $t('ภาพรวม') }}
            </b-card-title>
            <b-form-select
              v-model="chartPeriod"
              :options="$tOptions(chartPeriodOptions)"
              style="width: 140px;"
              size="sm"
            />
          </div>
          <div
            v-if="loadingChart"
            class="d-flex align-items-center justify-content-center"
            style="flex:1;min-height:300px;"
          >
            <b-spinner variant="primary" />
          </div>
          <div
            v-else
            style="flex:1;min-height:300px;"
          >
            <vue-apex-charts
              type="line"
              height="100%"
              :options="mainChartOptions"
              :series="mainChartSeries"
            />
          </div>
        </b-card>
      </b-col>
      <!-- MTD Summary -->
      <b-col
        md="4"
        cols="12"
      >
        <b-card>
          <div class="d-flex justify-content-between align-items-start mb-75">
            <div>
              <b-card-title class="mb-0">
                {{ $t('ยอดรายเดือน') }}
              </b-card-title>
              <small class="text-secondary-label">{{ currentMonthFull }}</small>
            </div>
            <b-form-select
              v-model="mtdMonth"
              :options="mtdMonthOptions"
              style="width: 110px;"
              size="sm"
            />
          </div>

          <div style="min-height:320px;">
            <template v-if="loadingMonthly">
              <div
                v-for="i in 3"
                :key="i"
                style="margin-bottom:16px;"
              >
                <div style="margin-bottom:4px;">
                  <b-skeleton
                    width="60%"
                    height="18px"
                    class="mb-0"
                  />
                </div>
                <b-skeleton
                  width="45%"
                  height="31px"
                  class="mb-0"
                />
                <div style="margin-top:6px;">
                  <b-skeleton
                    width="100%"
                    height="16px"
                    class="mb-0"
                  />
                </div>
                <div style="margin-top:4px;">
                  <b-skeleton
                    width="55%"
                    height="17px"
                    class="mb-0"
                  />
                </div>
              </div>
            </template>
            <template v-else>
              <!-- ยอดแทง -->
              <div style="margin-bottom:16px;">
                <div
                  class="text-secondary-label"
                  style="font-size:12px;margin-bottom:4px;"
                >
                  {{ $t('ยอดแทงหวย') }}
                </div>
                <div style="font-size:26px;font-weight:700;line-height:1.2;">
                  {{ toNumber(Math.round(selectedMonthMTD.bet)) }}
                </div>
                <div
                  v-if="monthlyBarData.bet.length > 0"
                  class="mtd-mini-bar-chart"
                >
                  <div
                    v-for="(height, index) in getMiniChartHeights(monthlyBarData.bet)"
                    :key="'bet-'+index"
                    :style="{
                      flex: 1,
                      height: height + '%',
                      backgroundColor: 'var(--hdg-mtd-bet-bar)',
                      borderRadius: '1px 1px 0 0',
                      minHeight: '2px'
                    }"
                  />
                </div>
                <div
                  style="font-size:11px;font-weight:600;margin-top:4px;"
                  :class="selectedMtdBetChange >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ selectedMtdBetChange >= 0 ? '⬆' : '⬇' }} {{ Math.abs(selectedMtdBetChange) }}% {{ $t('vs เดือนก่อน') }}
                </div>
              </div>

              <!-- ยอดจ่าย -->
              <div style="margin-bottom:16px;">
                <div
                  class="text-secondary-label"
                  style="font-size:12px;margin-bottom:4px;"
                >
                  {{ $t('ยอดจ่ายเงิน') }}
                </div>
                <div style="font-size:26px;font-weight:700;line-height:1.2;">
                  {{ toNumber(Math.round(selectedMonthMTD.pay)) }}
                </div>
                <div
                  v-if="monthlyBarData.pay.length > 0"
                  class="mtd-mini-bar-chart"
                >
                  <div
                    v-for="(height, index) in getMiniChartHeights(monthlyBarData.pay)"
                    :key="'pay-'+index"
                    :style="{
                      flex: 1,
                      height: height + '%',
                      backgroundColor: 'var(--hdg-mtd-pay-bar)',
                      borderRadius: '1px 1px 0 0',
                      minHeight: '2px'
                    }"
                  />
                </div>
                <div
                  style="font-size:11px;font-weight:600;margin-top:4px;"
                  :class="selectedMtdPayChange >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ selectedMtdPayChange >= 0 ? '⬆' : '⬇' }} {{ Math.abs(selectedMtdPayChange) }}% vs เดือนก่อน
                </div>
              </div>

              <!-- กำไร -->
              <div style="margin-bottom:16px;">
                <div
                  class="text-secondary-label"
                  style="font-size:12px;margin-bottom:4px;"
                >
                  กำไร
                </div>
                <div
                  style="font-size:26px;font-weight:700;line-height:1.2;"
                  :class="selectedMonthMTD.income > 0 ? 'text-success' : selectedMonthMTD.income < 0 ? 'text-danger' : ''"
                >
                  {{ toNumber(Math.round(selectedMonthMTD.income)) }}
                </div>
                <div
                  v-if="monthlyBarData.income.length > 0"
                  class="mtd-mini-bar-chart"
                >
                  <div
                    v-for="(height, index) in getMiniChartHeights(monthlyBarData.income)"
                    :key="'inc-'+index"
                    :style="{
                      flex: 1,
                      height: height + '%',
                      backgroundColor: 'var(--hdg-mtd-profit-bar)',
                      borderRadius: '1px 1px 0 0',
                      minHeight: '2px'
                    }"
                  />
                </div>
                <div
                  style="font-size:11px;font-weight:600;margin-top:4px;"
                  :class="selectedMtdIncomeChange >= 0 ? 'text-success' : 'text-danger'"
                >
                  {{ selectedMtdIncomeChange >= 0 ? '⬆' : '⬇' }} {{ Math.abs(selectedMtdIncomeChange) }}% vs เดือนก่อน
                </div>
              </div>

            </template>
          </div>
          <hr>
          <b-row>
            <b-col cols="6">
              <small class="text-secondary-label">{{ $t('Profit Margin') }}</small>
              <h5 class="font-weight-bolder">
                {{ selectedMonthMTD.bet > 0 ? ((selectedMonthMTD.income / selectedMonthMTD.bet) * 100).toFixed(1) : 0 }}%
              </h5>
            </b-col>
            <b-col cols="6">
              <small class="text-secondary-label">{{ $t('Win Rate') }}</small>
              <h5 class="font-weight-bolder">
                {{ selectedMonthMTD.bet > 0 ? (((selectedMonthMTD.bet - selectedMonthMTD.pay) / selectedMonthMTD.bet) * 100).toFixed(1) : 0 }}%
              </h5>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tabs: Lottery Types / Lottery Rounds -->
    <b-row>
      <b-col cols="12">
        <b-card no-body>
          <b-card-body>
            <b-tabs
              v-model="lotteryTabIndex"
              content-class="mt-1"
              nav-wrapper-class="lottery-tabs-nav"
            >
              <template #tabs-start>
                <b-card-title class="mb-0 mr-auto align-self-center">
                  สรุปยอดทุกหวย
                </b-card-title>
              </template>
              <!-- Tab: รายหวย -->
              <b-tab
                title="รายหวย"
                active
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <b-form-input
                    v-model="searchLotteryType"
                    placeholder="ค้นหาประเภทหวย..."
                    style="max-width: 250px;"
                    size="sm"
                    debounce="300"
                  />
                  <b-form-select
                    v-model="lotteryTypePeriod"
                    :options="$tOptions(periodOptionsWithDay)"
                    style="width: 140px;"
                    size="sm"
                  />
                </div>
                <div v-if="loadingLotteryTypes">
                  <div
                    v-for="i in 8"
                    :key="i"
                    class="d-flex align-items-center py-75"
                    :style="i % 2 === 0 ? { background: isDark ? 'rgba(255,255,255,0.04)' : '#f8f8f8' } : {}"
                  >
                    <div style="flex:2;padding:0 8px;">
                      <b-skeleton width="70%" />
                    </div>
                    <div style="flex:1;padding:0 8px;">
                      <b-skeleton width="60%" />
                    </div>
                    <div style="flex:1;padding:0 8px;">
                      <b-skeleton width="60%" />
                    </div>
                    <div style="flex:1;padding:0 8px;">
                      <b-skeleton width="50%" />
                    </div>
                    <div style="flex:1;padding:0 8px;">
                      <b-skeleton width="40%" />
                    </div>
                    <div style="flex:1;padding:0 8px;">
                      <b-skeleton
                        width="80px"
                        height="20px"
                      />
                    </div>
                  </div>
                </div>
                <b-table
                  v-else
                  :items="lotteryTypeItems"
                  :fields="$tFields(lotteryTypeFields)"
                  responsive
                  striped
                  hover
                  show-empty
                  empty-text="ไม่มีข้อมูลในช่วงเวลานี้"
                  :sort-by.sync="lotteryTypeSortBy"
                  :sort-desc.sync="lotteryTypeSortDesc"
                  :per-page="lotteryTypePerPage"
                  :current-page="lotteryTypeCurrentPage"
                >
                  <template #cell(type)="data">
                    <span class="font-weight-bolder">{{ data.value }}</span>
                  </template>
                  <template #cell(bet)="data">
                    <span class="font-weight-bolder">{{ toNumber(data.value) }}</span>
                  </template>
                  <template #cell(pay)="data">
                    <span class="font-weight-bolder">{{ toNumber(data.value) }}</span>
                  </template>
                  <template #cell(profit)="data">
                    <span
                      class="font-weight-bolder"
                      :class="data.value > 0 ? 'text-success' : data.value < 0 ? 'text-danger' : ''"
                    >
                      {{ data.value > 0 ? '+' : '' }}{{ toNumber(data.value) }}
                    </span>
                  </template>
                  <template #cell(percentage)="data">
                    <span class="font-weight-bolder">{{ data.value }}%</span>
                  </template>
                  <template #cell(changePercent)="data">
                    <span
                      class="font-weight-bolder"
                      :class="data.value > 0 ? 'text-success' : data.value < 0 ? 'text-danger' : 'text-muted'"
                    >
                      <template v-if="data.value === 0">-</template>
                      <template v-else>{{ data.value > 0 ? '⬆' : '⬇' }} {{ Math.abs(data.value) }}%</template>
                    </span>
                  </template>
                  <template #cell(trendData)="data">
                    <div style="display:inline-flex;align-items:center;justify-content:center;width:80px;height:24px;">
                      <svg
                        v-if="data.value && data.value.length > 0"
                        viewBox="0 0 80 24"
                        style="width: 80px; height: 24px;"
                      >
                        <path
                          :d="generateSparkline(data.value)"
                          fill="none"
                          :stroke="data.item.changePercent >= 0 ? 'var(--hdg-success)' : 'var(--hdg-danger)'"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <span
                        v-else
                        class="text-muted"
                        style="font-size:11px;"
                      >-</span>
                    </div>
                  </template>
                </b-table>
                <div
                  v-if="!loadingLotteryTypes && lotteryTypeItems.length > 0"
                  class="d-flex justify-content-between align-items-center mt-1"
                >
                  <div
                    class="d-flex align-items-center text-muted"
                    style="font-size: 13px;"
                  >
                    <span class="mr-50">แสดง</span>
                    <b-form-select
                      v-model="lotteryTypePerPage"
                      :options="[10, 25, 50, 100, 250]"
                      size="sm"
                      style="width: 70px;"
                      @change="lotteryTypeCurrentPage = 1"
                    />
                    <span class="ml-50">จาก {{ lotteryTypeItems.length }} รายการ</span>
                  </div>
                  <b-pagination
                    v-if="lotteryTypeItems.length > lotteryTypePerPage"
                    v-model="lotteryTypeCurrentPage"
                    :total-rows="lotteryTypeItems.length"
                    :per-page="lotteryTypePerPage"
                    size="sm"
                    class="mb-0"
                  />
                </div>
              </b-tab>

              <!-- Tab: รายรอบ -->
              <b-tab title="รายรอบ">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <b-form-input
                    v-model="searchRounds"
                    placeholder="ค้นหา..."
                    style="max-width: 250px;"
                    size="sm"
                    debounce="300"
                  />
                  <div
                    class="d-flex align-items-center"
                    style="gap: 8px;"
                  >
                    <b-button-group size="sm">
                      <b-button
                        :variant="isHuay ? 'primary' : 'outline-primary'"
                        @click="isHuay = true"
                      >
                        หวย
                      </b-button>
                      <b-button
                        :variant="!isHuay ? 'primary' : 'outline-primary'"
                        @click="isHuay = false"
                      >
                        ยี่กี
                      </b-button>
                    </b-button-group>
                    <b-form-select
                      v-model="roundsPeriod"
                      :options="$tOptions(periodOptionsWithDay)"
                      size="sm"
                      style="width: 140px;"
                    />
                  </div>
                </div>
                <b-table
                  :items="loadingRounds ? roundSkeletonItems : roundItems"
                  :fields="$tFields(roundFields)"
                  responsive
                  striped
                  hover
                  show-empty
                  empty-text="ไม่มีข้อมูลรายรอบ"
                >
                  <template #cell()="data">
                    <div
                      v-if="loadingRounds"
                      :style="{ display: 'flex', justifyContent: (data.field.tdClass || '').includes('text-right') ? 'flex-end' : (data.field.tdClass || '').includes('text-center') ? 'center' : 'flex-start' }"
                    >
                      <b-skeleton
                        :width="{ type: '75%', roundId: '55%', date: '50%', bet: '45%', pay: '45%', profit: '40%' }[data.field.key] || '50%'"
                        height="20px"
                        class="mb-0"
                      />
                    </div>
                    <template v-else-if="data.field.key === 'type'">
                      <span class="font-weight-bolder">{{ data.value }}</span>
                    </template>
                    <template v-else-if="data.field.key === 'bet' || data.field.key === 'pay'">
                      <span class="font-weight-bolder">{{ toNumber(data.value) }}</span>
                    </template>
                    <template v-else-if="data.field.key === 'profit'">
                      <span
                        class="font-weight-bolder"
                        :class="data.value > 0 ? 'text-success' : data.value < 0 ? 'text-danger' : ''"
                      >
                        {{ data.value > 0 ? '+' : '' }}{{ toNumber(data.value) }}
                      </span>
                    </template>
                    <template v-else>
                      {{ data.value }}
                    </template>
                  </template>
                </b-table>
                <div
                  v-if="loadingRounds || roundsPagination.totalItems > 0"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div
                    class="d-flex align-items-center text-muted"
                    style="font-size: 13px;"
                  >
                    <span class="mr-50">แสดง</span>
                    <b-form-select
                      v-model="roundsPagination.pageSize"
                      :options="[10, 25, 50, 100, 250]"
                      size="sm"
                      style="width: 70px;"
                      @change="roundPage = 1; loadLotteryRounds()"
                    />
                    <span class="ml-50">จาก {{ roundsPagination.totalItems }} รายการ</span>
                  </div>
                  <b-pagination
                    v-if="roundsPagination.totalPages > 1"
                    v-model="roundPage"
                    :total-rows="roundsPagination.totalItems"
                    :per-page="roundsPagination.pageSize"
                    first-number
                    last-number
                    align="right"
                    size="sm"
                    class="mb-0"
                  />
                </div>
              </b-tab>
            </b-tabs>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Gov Lottery Modal -->
    <b-modal
      v-model="showGovLotteryModal"
      title="หวยรัฐบาล - 6 งวดล่าสุด"
      size="lg"
      hide-footer
      centered
      body-class="gov-modal-body"
    >
      <div
        v-if="loadingGovRounds"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <template v-else>
        <!-- Stats Summary -->
        <div
          class="d-flex flex-column flex-sm-row mb-1"
          style="gap: 1rem;"
        >
          <div
            v-for="stat in [
              { label: 'ค่าเฉลี่ยยอดแทง', value: govLotteryStats.avgBet },
              { label: 'ค่าเฉลี่ยยอดจ่าย', value: govLotteryStats.avgPay },
              { label: 'ค่าเฉลี่ยกำไร', value: govLotteryStats.avgProfit, colored: true },
            ]"
            :key="stat.label"
            class="flex-fill text-center py-1"
            style="border: 1px solid #eee4e7; border-radius: 0.428rem;"
          >
            <small class="text-muted">{{ $t(stat.label) }}</small>
            <h4
              class="font-weight-bolder mb-0"
              :class="stat.colored ? (stat.value > 0 ? 'text-success' : stat.value < 0 ? 'text-danger' : '') : ''"
            >
              {{ toNumber(stat.value) }}
            </h4>
          </div>
        </div>

        <!-- Gov Lottery Table -->
        <b-table
          :items="govLotteryRounds"
          :fields="$tFields(govLotteryFields)"
          responsive
          striped
          hover
          small
          class="gov-table"
        >
          <template #cell(roundDate)="data">
            <span class="font-weight-bolder">{{ data.value }}</span>
          </template>
          <template #cell(bet)="data">
            <span class="font-weight-bolder">{{ toNumber(data.value) }}</span>
          </template>
          <template #cell(pay)="data">
            <span class="font-weight-bolder">{{ toNumber(data.value) }}</span>
          </template>
          <template #cell(profit)="data">
            <span
              class="font-weight-bolder"
              :class="data.value > 0 ? 'text-success' : data.value < 0 ? 'text-danger' : ''"
            >
              {{ data.value > 0 ? '+' : '' }}{{ toNumber(data.value) }}
            </span>
          </template>
          <template #cell(profitPercent)="data">
            <span
              class="font-weight-bolder"
              :class="data.item.profit > 0 ? 'text-success' : data.item.profit < 0 ? 'text-danger' : ''"
            >
              {{ data.value }}%
            </span>
          </template>
        </b-table>

        <!-- Mini Bar Chart: เทรนด์ยอดแทง 6 งวด -->
        <div
          v-if="govLotteryRounds.length > 0"
          class="mt-1"
        >
          <small class="text-muted d-block mb-50">{{ $t('เทรนด์ยอดแทง 6 งวด') }}</small>
          <div style="display:flex;align-items:flex-end;gap:2px;height:60px;overflow:hidden;">
            <div
              v-for="(round, index) in govLotteryRoundsReversed"
              :key="index"
              :style="{
                flex: '1 1 0',
                minWidth: '0',
                height: (round.bet / govLotteryMaxBet * 100) + '%',
                backgroundColor: 'rgb(54, 162, 235)',
                opacity: 0.8,
                borderRadius: '2px 2px 0 0',
                minHeight: '10px',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                paddingBottom: '4px',
              }"
            >
              <span style="font-size:9px;color:#fff;font-weight:600;">{{ round.roundDate }}</span>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </section>
</template>

<script>
/* eslint-disable */
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BTable,
  BTabs,
  BTab,
  BPagination,
  BSpinner,
  BFormSelect,
  BFormInput,
  BFormCheckbox,
  BFormGroup,
  BButtonGroup,
  BButton,
  BModal,
  BSkeleton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import reportService from '@/service/reportService'

const FILTER_STORAGE_KEY = 'huay-dashboard-filters'
function loadSavedFilters() {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    return stored ? JSON.parse(stored) : null
  } catch { return null }
}
function saveFilters(filters) {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
}

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BTable,
    BTabs,
    BTab,
    BPagination,
    BSpinner,
    BFormSelect,
    BFormInput,
    BFormCheckbox,
    BFormGroup,
    BButtonGroup,
    BButton,
    BModal,
    VueApexCharts,
    AgentSelector,
    BSkeleton,
  },
  data() {
    const today = new Date()
    const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
    const saved = loadSavedFilters()
    // เก็บไว้ให้ mounted() ใช้ re-apply หลัง b-tabs reset (ไม่ต้องอ่าน localStorage ซ้ำ)
    this._savedTabIndex = saved?.lotteryTabIndex
    return {
      // Agent filter
      selectedAgent: saved?.selectedAgent || null,
      includeDownlines: saved?.includeDownlines || false,

      // Loading states
      loadingOverview: true,
      loadingMiniCharts: true,
      loadingChart: true,
      loadingLotteryTypes: true,
      loadingRounds: false,
      loadingMonthly: true,
      loadingGovRounds: false,

      // API data
      apiOverview: {},
      apiMiniCharts: { labels: [], bet: [], pay: [], income: [] },
      apiChart: { labels: [], bet: [], pay: [], income: [] },
      apiLotteryTypes: [],
      apiLotteryRounds: [],
      apiMonthlyHistory: [],
      govLotteryRounds: [],

      // Chart period
      chartPeriod: saved?.chartPeriod || '7days',
      chartPeriodOptions: [
        { value: '7days', text: '7 วัน' },
        { value: '1month', text: '1 เดือน' },
        { value: '3months', text: '3 เดือน' },
        { value: '1year', text: '1 ปี' },
      ],

      // MTD month selector
      mtdMonth: currentMonthKey,

      // Lottery types
      lotteryTabIndex: saved?.lotteryTabIndex || 0,
      lotteryTypePeriod: saved?.lotteryTypePeriod || '7days',
      periodOptionsWithDay: [
        { value: '1day', text: '1 วัน' },
        { value: '7days', text: '7 วัน' },
        { value: '1month', text: '1 เดือน' },
        { value: '3months', text: '3 เดือน' },
        { value: '1year', text: '1 ปี' },
      ],
      searchLotteryType: '',
      lotteryTypeSortBy: 'bet',
      lotteryTypeSortDesc: true,
      lotteryTypePerPage: 10,
      lotteryTypeCurrentPage: 1,

      // Lottery rounds
      isHuay: saved?.isHuay !== undefined ? saved.isHuay : true,
      roundsPeriod: saved?.roundsPeriod || '7days',
      searchRounds: '',
      roundPage: 1,
      roundsPagination: {
        page: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
      },

      // Gov lottery modal
      showGovLotteryModal: false,

      // Table fields
      lotteryTypeFields: [
        { key: 'type', label: 'ประเภทหวย', sortable: true, thStyle: { minWidth: '120px' } },
        { key: 'bet', label: 'ยอดแทง', sortable: true, tdClass: 'text-right', thClass: 'text-right', thStyle: { minWidth: '100px' } },
        { key: 'pay', label: 'ยอดจ่าย', sortable: true, tdClass: 'text-right', thClass: 'text-right', thStyle: { minWidth: '100px' } },
        { key: 'profit', label: 'กำไร', sortable: true, tdClass: 'text-right', thClass: 'text-right', thStyle: { minWidth: '80px' } },
        { key: 'percentage', label: '% สัดส่วน', sortable: true, tdClass: 'text-center', thClass: 'text-center text-nowrap', thStyle: { minWidth: '90px' } },
        { key: 'changePercent', label: 'เทรนด์', sortable: true, tdClass: 'text-center', thClass: 'text-center', thStyle: { minWidth: '80px' } },
        { key: 'trendData', label: 'กราฟ 7 วัน', tdClass: 'text-center', thClass: 'text-center', thStyle: { minWidth: '80px' } },
      ],
      roundFields: [
        { key: 'type', label: 'ประเภทหวย', sortable: true, thStyle: { minWidth: '120px' } },
        { key: 'roundId', label: 'รอบ/งวด', sortable: true, tdClass: 'text-center', thClass: 'text-center', thStyle: { minWidth: '100px' } },
        { key: 'date', label: 'วันที่', sortable: true, tdClass: 'text-center', thClass: 'text-center', thStyle: { minWidth: '100px' } },
        { key: 'bet', label: 'ยอดแทง', sortable: true, tdClass: 'text-right', thClass: 'text-right', thStyle: { minWidth: '100px' } },
        { key: 'pay', label: 'ยอดจ่าย', sortable: true, tdClass: 'text-right', thClass: 'text-right', thStyle: { minWidth: '100px' } },
        { key: 'profit', label: 'กำไร', sortable: true, tdClass: 'text-right', thClass: 'text-right', thStyle: { minWidth: '80px' } },
      ],
      govLotteryFields: [
        { key: 'roundDate', label: 'งวด', sortable: true, tdClass: 'text-nowrap', thClass: 'text-nowrap', thStyle: { minWidth: '80px' } },
        { key: 'fullDate', label: 'วันที่', sortable: true, tdClass: 'text-center text-nowrap', thClass: 'text-center text-nowrap', thStyle: { minWidth: '100px' } },
        { key: 'bet', label: 'ยอดแทง', sortable: true, tdClass: 'text-right text-nowrap', thClass: 'text-right text-nowrap', thStyle: { minWidth: '100px' } },
        { key: 'pay', label: 'ยอดจ่าย', sortable: true, tdClass: 'text-right text-nowrap', thClass: 'text-right text-nowrap', thStyle: { minWidth: '100px' } },
        { key: 'profit', label: 'กำไร', sortable: true, tdClass: 'text-right text-nowrap', thClass: 'text-right text-nowrap', thStyle: { minWidth: '80px' } },
        { key: 'profitPercent', label: '% กำไร', sortable: true, tdClass: 'text-center text-nowrap', thClass: 'text-center text-nowrap', thStyle: { minWidth: '70px' } },
      ],
    }
  },
  computed: {
    isMaster() {
      return this.$store.state.app.isMaster(this.$store)
    },
    skin() {
      return this.$store.state.appConfig.layout.skin
    },
    isDark() {
      return this.skin === 'dark'
    },
    chartForeColor() {
      return this.themeColor('--hdg-chart-fore', this.isDark)
    },
    chartPalette() {
      return [
        this.themeColor('--hdg-chart-bet', this.isDark),
        this.themeColor('--hdg-chart-pay', this.isDark),
        this.themeColor('--hdg-chart-profit', this.isDark),
      ]
    },
    chartGridBorderColor() {
      return this.themeColor('--hdg-chart-border', this.isDark)
    },
    todaySum() {
      return this.apiOverview?.today?.current || { bet: 0, pay: 0, income: 0 }
    },
    todayBetChange() {
      return this.apiOverview?.today?.changes?.betPercent || 0
    },
    todayPayChange() {
      return this.apiOverview?.today?.changes?.payPercent || 0
    },
    todayIncomeChange() {
      return this.apiOverview?.today?.changes?.incomePercent || 0
    },
    sevenDaysData() {
      return {
        labels: this.apiMiniCharts.labels || [],
        bet: this.apiMiniCharts.bet || [],
        pay: this.apiMiniCharts.pay || [],
        income: this.apiMiniCharts.income || [],
      }
    },
    playerInsights() {
      return this.apiOverview?.playerInsights || { totalPoy: 0, poyPerUser: 0, bigSpender: 0, avgBetPerUser: 0 }
    },
    govLottery() {
      return this.apiOverview?.govLottery || { roundDate: '-', bet: 0, pay: 0, profit: 0, betChangePercent: 0 }
    },
    // MTD
    mtdSum() {
      return this.apiOverview?.mtd?.current || { bet: 0, pay: 0, income: 0 }
    },
    isCurrentMonth() {
      const today = new Date()
      const currentKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
      return this.mtdMonth === currentKey
    },
    currentMonthFull() {
      if (!this.mtdMonth) return ''
      const [year, month] = this.mtdMonth.split('-').map(Number)
      return this.formatMonthFull(year, month)
    },
    mtdMonthOptions() {
      const today = new Date()
      const currentKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`
      const options = [{
        value: currentKey,
        text: this.formatMonthShort(today.getFullYear(), today.getMonth() + 1),
      }]
      const history = this.apiMonthlyHistory || []
      for (const item of history) {
        if (item.month && item.month !== currentKey) {
          const [year, month] = item.month.split('-').map(Number)
          options.push({
            value: item.month,
            text: this.formatMonthShort(year, month),
          })
        }
      }
      return options
    },
    selectedMonthMTD() {
      if (this.isCurrentMonth) return this.mtdSum
      const found = (this.apiMonthlyHistory || []).find(m => m.month === this.mtdMonth)
      return found || { bet: 0, pay: 0, income: 0 }
    },
    selectedMtdBetChange() {
      if (this.isCurrentMonth) return this.apiOverview?.mtd?.changes?.betPercent || 0
      return this.calcMonthChange('bet')
    },
    selectedMtdPayChange() {
      if (this.isCurrentMonth) return this.apiOverview?.mtd?.changes?.payPercent || 0
      return this.calcMonthChange('pay')
    },
    selectedMtdIncomeChange() {
      if (this.isCurrentMonth) return this.apiOverview?.mtd?.changes?.incomePercent || 0
      return this.calcMonthChange('income')
    },
    monthlyBarData() {
      const history = this.apiMonthlyHistory || []
      if (history.length === 0) return { labels: [], bet: [], pay: [], income: [] }
      const sorted = [...history].sort((a, b) => (a.month || '').localeCompare(b.month || ''))
      return {
        labels: sorted.map(m => m.month),
        bet: sorted.map(m => m.bet || 0),
        pay: sorted.map(m => m.pay || 0),
        income: sorted.map(m => m.income || 0),
      }
    },
    // Chart
    chartTickAmount() {
      const map = { '7days': 7, '1month': 10, '3months': 12, '1year': 12 }
      return map[this.chartPeriod] || 10
    },
    mainChartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: { show: false },
          zoom: { enabled: false },
          foreColor: this.chartForeColor,
        },
        stroke: {
          width: [3, 3, 3],
          curve: 'smooth',
        },
        colors: this.chartPalette,
        xaxis: {
          categories: this.chartLabelsWithToday,
          tickAmount: this.chartTickAmount,
          labels: {
            rotate: -45,
            rotateAlways: false,
          },
        },
        yaxis: {
          labels: {
            formatter: val => this.toNumber(Math.round(val)),
          },
        },
        tooltip: {
          theme: this.isDark ? 'dark' : 'light',
          y: {
            formatter: val => this.toNumber(Math.round(val)),
          },
        },
        legend: {
          position: 'top',
        },
        grid: {
          borderColor: this.chartGridBorderColor,
        },
      }
    },
    chartLabelsWithToday() {
      const labels = [...(this.apiChart.labels || [])]
      if (this.apiOverview?.today) {
        labels.push(this.$t('วันนี้'))
      }
      return labels
    },
    mainChartSeries() {
      const today = this.todaySum
      const hasToday = !!this.apiOverview?.today
      return [
        { name: this.$t('ยอดแทง'), data: [...(this.apiChart.bet || []), ...(hasToday ? [today.bet] : [])] },
        { name: this.$t('ยอดจ่าย'), data: [...(this.apiChart.pay || []), ...(hasToday ? [today.pay] : [])] },
        { name: this.$t('กำไร'), data: [...(this.apiChart.income || []), ...(hasToday ? [today.income] : [])] },
      ]
    },
    // Tables
    lotteryTypeItems() {
      return (this.apiLotteryTypes || []).map(item => ({
        ...item,
        profit: (item.bet || 0) - (item.pay || 0),
      }))
    },
    roundSkeletonItems() {
      return Array.from({ length: this.roundsPagination.pageSize }, (_, i) => ({ _id: i }))
    },
    roundItems() {
      return this.apiLotteryRounds || []
    },
    govLotteryStats() {
      const rounds = this.govLotteryRounds
      if (!rounds || rounds.length === 0) return { avgBet: 0, avgPay: 0, avgProfit: 0 }
      const totalBet = rounds.reduce((sum, r) => sum + (r.bet || 0), 0)
      const totalPay = rounds.reduce((sum, r) => sum + (r.pay || 0), 0)
      const totalProfit = rounds.reduce((sum, r) => sum + (r.profit || 0), 0)
      return {
        avgBet: Math.round(totalBet / rounds.length),
        avgPay: Math.round(totalPay / rounds.length),
        avgProfit: Math.round(totalProfit / rounds.length),
      }
    },
    govLotteryRoundsReversed() {
      return [...(this.govLotteryRounds || [])].reverse()
    },
    govLotteryMaxBet() {
      const rounds = this.govLotteryRounds || []
      if (rounds.length === 0) return 1
      return Math.max(...rounds.map(r => r.bet || 0)) || 1
    },
  },
  watch: {
    selectedAgent() { this.persistFilters() },
    includeDownlines() { this.persistFilters() },
    chartPeriod() {
      this.persistFilters()
      this.loadChart()
    },
    lotteryTypePeriod() {
      this.persistFilters()
      this.loadLotteryTypes()
    },
    searchLotteryType() {
      this.loadLotteryTypes()
    },
    searchRounds() {
      this.roundPage = 1
      this.loadLotteryRounds()
    },
    roundPage() {
      this.loadLotteryRounds()
    },
    roundsPeriod() {
      this.persistFilters()
      this.roundPage = 1
      this.loadLotteryRounds()
    },
    isHuay() {
      this.persistFilters()
      this.roundPage = 1
      this.loadLotteryRounds()
    },
    lotteryTabIndex(val) {
      this.persistFilters()
      if (val === 1 && this.apiLotteryRounds.length === 0) {
        this.loadLotteryRounds()
      }
    },
    showGovLotteryModal(val) {
      if (val && this.govLotteryRounds.length === 0) {
        this.loadGovLotteryRounds()
      }
    },
  },
  mounted() {
    // Restore tab index หลัง b-tabs render เสร็จ (ใช้ค่าที่เก็บไว้จาก data())
    if (this._savedTabIndex !== undefined) {
      this.$nextTick(() => {
        this.lotteryTabIndex = this._savedTabIndex
      })
    }

    // AgentSelector จะ auto-select แล้ว trigger @change → reloadAll()
    // ถ้าไม่ใช่ master (ไม่มี AgentSelector) ต้องเรียกเอง
    if (!this.isMaster) {
      this.reloadAll()
    }
  },
  methods: {
    themeColor(tokenName, isDark = false) {
      if (typeof window === 'undefined') return `var(${tokenName})`
      const sources = isDark
        ? [document.body, document.documentElement].filter(Boolean)
        : [document.documentElement, document.body].filter(Boolean)
      for (const source of sources) {
        const value = window.getComputedStyle(source).getPropertyValue(tokenName).trim()
        if (value) return value
      }
      return `var(${tokenName})`
    },
    persistFilters() {
      saveFilters({
        selectedAgent: this.selectedAgent,
        includeDownlines: this.includeDownlines,
        chartPeriod: this.chartPeriod,
        lotteryTypePeriod: this.lotteryTypePeriod,
        lotteryTabIndex: this.lotteryTabIndex,
        isHuay: this.isHuay,
        roundsPeriod: this.roundsPeriod,
      })
    },
    toNumber(v) {
      return this.$store.state.app.toNumber(v)
    },
    monthLocale() {
      return String(this.$i18n.locale).startsWith('th') ? 'th-TH-u-ca-buddhist' : 'en-US'
    },
    formatMonthFull(year, month) {
      return new Intl.DateTimeFormat(this.monthLocale(), {
        month: 'long',
        year: 'numeric',
      }).format(new Date(year, month - 1, 1))
    },
    formatMonthShort(year, month) {
      return new Intl.DateTimeFormat(this.monthLocale(), {
        month: 'short',
        year: '2-digit',
      }).format(new Date(year, month - 1, 1))
    },
    shortNumber(v) {
      const n = Number(v)
      if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M'
      if (Math.abs(n) >= 1e4) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'k'
      return this.toNumber(n)
    },
    getMiniChartHeights(data) {
      if (!data || data.length === 0) return []
      const max = Math.max(...data)
      const min = Math.min(...data)
      const range = max - min
      if (range === 0) return data.map(() => 50)
      return data.map(val => ((val - min) / range) * 100)
    },
    calcMonthChange(field) {
      const history = this.apiMonthlyHistory || []
      const sorted = [...history].sort((a, b) => (a.month || '').localeCompare(b.month || ''))
      const idx = sorted.findIndex(m => m.month === this.mtdMonth)
      if (idx <= 0) return 0
      const curr = sorted[idx][field] || 0
      const prev = sorted[idx - 1][field] || 0
      if (prev === 0) return 0
      return Number(((curr - prev) / Math.abs(prev) * 100).toFixed(1))
    },
    buildAgentParams() {
      const params = new URLSearchParams()
      if (this.isMaster && this.selectedAgent && this.selectedAgent.code) {
        params.set('agent', this.selectedAgent.code)
      }
      if (this.includeDownlines) {
        params.set('includeDownlines', 'true')
      }
      return params.toString()
    },
    buildUrl(path, extraParams) {
      const agentParams = this.buildAgentParams()
      const parts = [agentParams, extraParams].filter(Boolean)
      const qs = parts.join('&')
      return qs ? `${path}?${qs}` : path
    },
    onAgentChange() {
      if (!this.selectedAgent || !this.selectedAgent.code || this.selectedAgent.code === 'master') {
        this.includeDownlines = false
      }
      this.reloadAll()
    },
    reloadAll() {
      this.loadOverview()
      this.loadMiniCharts()
      this.loadChart()
      this.loadLotteryTypes()
      this.loadMonthlyHistory()
      if (this.lotteryTabIndex === 1) {
        this.loadLotteryRounds()
      }
      // Reset gov lottery data so it reloads on next modal open
      this.govLotteryRounds = []
      if (this.showGovLotteryModal) {
        this.loadGovLotteryRounds()
      }
    },
    async loadOverview() {
      if (!this.apiOverview || !this.apiOverview.today) this.loadingOverview = true
      try {
        const url = this.buildUrl('huay-dashboard/overview')
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.apiOverview = res.data
        }
      } catch (e) {
        // silently fail
      } finally {
        this.loadingOverview = false
      }
    },
    async loadMiniCharts() {
      if (!this.apiMiniCharts.bet || this.apiMiniCharts.bet.length === 0) this.loadingMiniCharts = true
      try {
        const url = this.buildUrl('huay-dashboard/mini-charts')
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.apiMiniCharts = res.data
        }
      } catch (e) {
        // silently fail
      } finally {
        this.loadingMiniCharts = false
      }
    },
    async loadChart() {
      if (!this.apiChart.labels || this.apiChart.labels.length === 0) this.loadingChart = true
      try {
        const url = this.buildUrl('huay-dashboard/chart', `period=${this.chartPeriod}`)
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.apiChart = res.data
        }
      } catch (e) {
        // silently fail
      } finally {
        this.loadingChart = false
      }
    },
    async loadLotteryTypes() {
      if (this.apiLotteryTypes.length === 0) this.loadingLotteryTypes = true
      try {
        const extra = new URLSearchParams({ period: this.lotteryTypePeriod })
        if (this.searchLotteryType) {
          extra.set('search', this.searchLotteryType)
        }
        const url = this.buildUrl('huay-dashboard/lottery-types', extra.toString())
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.apiLotteryTypes = res.data
        }
      } catch (_) { /* ignored */
      } finally {
        this.loadingLotteryTypes = false
      }
    },
    async loadLotteryRounds() {
      this.loadingRounds = true
      const start = Date.now()
      try {
        const extra = new URLSearchParams({
          period: this.roundsPeriod,
          page: this.roundPage,
          pageSize: this.roundsPagination.pageSize,
          sortBy: 'date',
          order: 'desc',
          isHuay: this.isHuay,
        })
        if (this.searchRounds) {
          extra.set('search', this.searchRounds)
        }
        const url = this.buildUrl('huay-dashboard/lottery-rounds', extra.toString())
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.apiLotteryRounds = res.data.data || []
          this.roundsPagination = res.data.pagination || this.roundsPagination
        }
      } catch (e) {
        // silently fail
      } finally {
        const elapsed = Date.now() - start
        if (elapsed < 150) await new Promise(r => setTimeout(r, 150 - elapsed))
        this.loadingRounds = false
      }
    },
    async loadMonthlyHistory() {
      if (this.apiMonthlyHistory.length === 0) this.loadingMonthly = true
      try {
        const url = this.buildUrl('huay-dashboard/monthly-history', 'months=12')
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.apiMonthlyHistory = res.data
        }
      } catch (e) {
        // silently fail
      } finally {
        this.loadingMonthly = false
      }
    },
    async loadGovLotteryRounds() {
      this.loadingGovRounds = true
      try {
        const url = this.buildUrl('huay-dashboard/gov-lottery-rounds', 'limit=6')
        const res = await reportService.getAuthen(url)
        if (res.status === 200 && res.data) {
          this.govLotteryRounds = res.data
        }
      } catch (e) {
        // silently fail
      } finally {
        this.loadingGovRounds = false
      }
    },
    generateSparkline(data, width = 80, height = 24) {
      if (!data || data.length === 0) return ''
      const padding = 4
      const gw = width - padding * 2
      const gh = height - padding * 2
      const max = Math.max(...data)
      const min = Math.min(...data)
      const range = max - min === 0 ? 1 : max - min
      const points = data.map((val, i) => ({
        x: padding + (i / (data.length - 1)) * gw,
        y: padding + gh - ((val - min) / range) * gh,
      }))
      if (points.length < 2) return ''
      let path = `M ${points[0].x},${points[0].y}`
      for (let i = 0; i < points.length - 1; i++) {
        const cur = points[i]
        const next = points[i + 1]
        const cx = (cur.x + next.x) / 2
        path += ` Q ${cx},${cur.y} ${cx},${(cur.y + next.y) / 2}`
        path += ` Q ${cx},${next.y} ${next.x},${next.y}`
      }
      return path
    },
  },
}
</script>

<style lang="scss">
#huay-dashboard {
  .text-secondary-label,
  .insight-label {
    color: var(--hdg-body);
  }

  .cursor-pointer {
    cursor: pointer;
  }

  .lottery-tabs-nav .nav {
    display: flex;
    align-items: center;
  }

  // Summary Cards (Gradient)
  .summary-card {
    border-radius: 6px;
    color: #fff;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;

    @media (max-width: 991.98px) {
      padding: 12px;
    }

    @media (max-width: 575.98px) {
      margin-bottom: 1.2rem;
    }

    .summary-card-header {
      font-size: 14px;
      font-weight: 500;
    }

    .summary-card-body {
      display: flex;
      align-items: flex-end;
    }

    .summary-card-left {
      flex: 0 0 35%;
    }

    .summary-card-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background-color: rgba(0, 0, 0, 0.18);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
    }

    .mini-bar-chart {
      display: flex;
      align-items: flex-end;
      gap: 1px;
      height: 28px;
      margin-top: -5px;
    }

    .mini-bar {
      flex: 1;
      background-color: rgba(255, 255, 255, 0.5);
      border-radius: 1px 1px 0 0;
      min-height: 3px;
    }

    .summary-card-right {
      flex: 0 0 65%;
      text-align: right;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-self: stretch;
    }

    .summary-card-value {
      font-size: 30px;
      font-weight: 600;
      line-height: 1;

      @media (max-width: 991.98px) {
        font-size: 22px;
      }
    }

    .summary-card-change {
      margin-top: 0;
    }
  }

  .summary-card-bet {
    background: var(--hdg-summary-bet-gradient);
  }

  .summary-card-pay {
    background: var(--hdg-summary-pay-gradient);
  }

  .summary-card-profit {
    background: var(--hdg-summary-profit-gradient);
  }

  // Insight Cards (Player Insights)
  .insight-card {
    height: 120px;
    overflow: hidden;
    .card-body {
      padding: 12px 16px;

      @media (max-width: 991.98px) {
        padding: 10px 12px;
      }
    }

    .insight-label {
      font-size: 13px;
      color: var(--hdg-body);
      white-space: nowrap;
    }

    .insight-value {
      font-size: 26px;
      font-weight: 600;
      flex-shrink: 0;

      @media (max-width: 991.98px) {
        font-size: 20px;
      }
    }
  }

  // Gov Lottery Card
  .gov-lottery-card {
    height: 120px;
    border-radius: 6px;
    background: var(--hdg-gov-card-gradient);
    color: #fff;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
    overflow: hidden;
    transition: transform 0.15s, box-shadow 0.15s;

    @media (max-width: 991.98px) {
      padding: 10px 10px;
      font-size: 12px;
    }

    @media (max-width: 575.98px) {
      margin-bottom: 1.2rem;
    }

    .gov-lottery-icon {
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      .gov-lottery-icon {
        opacity: 1;
      }
    }
  }

  // MTD Mini Bar Chart
  .mtd-mini-bar-chart {
    display: flex;
    align-items: flex-end;
    gap: 1px;
    height: 16px;
    margin-top: 6px;
  }
}

.skeleton-light .b-skeleton,
.skeleton-light.b-skeleton {
  background-color: rgba(255, 255, 255, 0.3) !important;
}
.skeleton-light .b-skeleton::after,
.skeleton-light.b-skeleton::after {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent) !important;
}
.skeleton-bar {
  background: rgba(255, 255, 255, 0.2) !important;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}
@keyframes skeleton-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}

#huay-dashboard .agent-filter-checkbox {
  margin-bottom: 0;
}
#huay-dashboard .agent-filter-checkbox .custom-control-label {
  margin-bottom: 0;
}

@media (max-width: 575px) {
  .agent-select-wrapper {
    max-width: 100% !important;
  }
  .agent-filter-checkbox {
    margin-left: auto;
  }
}

.gov-modal-body {
  padding: 1.4rem !important;
}

@media (max-width: 575.98px) {
  .gov-table {
    font-size: 13px;
  }
}

.dark-layout #huay-dashboard {
  .text-secondary-label,
  .insight-label {
    color: var(--hdg-body);
  }

  .agent-filter-checkbox {
    .custom-control-input:disabled ~ .custom-control-label {
      opacity: 0.4;
    }
  }
}
</style>
